import * as jotai from 'jotai'
import * as React from 'react'

import * as hooks from '@owl-nest/hooks'
import * as api from '@owl-nest/api-client/next'

import * as base from './base'

export const SHARED_ADDRESSES_CACHE = jotai.atom<hooks.Cache<api.Addresses>>({})

type UseAddressOptions<EXTRA_FIELDS extends string> = {
  fetchOnMount?: boolean | 'force'
  extraFields?: EXTRA_FIELDS[]
}

export function useAddress<EXTRA_FIELDS extends string = ''>(
  userId: number | undefined,
  { fetchOnMount = true, extraFields = [] }: UseAddressOptions<EXTRA_FIELDS> = {},
) {
  const sharedQuery = hooks.useSharedQuery(
    (userId: number, extraFields: EXTRA_FIELDS[]) => {
      return base.load(userId, extraFields)
    },
    {
      atom: SHARED_ADDRESSES_CACHE,
      id,
    },
  )

  hooks.useFetchOnMount({ fetchOnMount, query, getResponse }, id(userId, extraFields))

  return { response: getResponse(), create, update, remove }

  function query(): Promise<void> | undefined {
    if (userId) {
      return sharedQuery.query(userId, extraFields)
    }
  }

  function getResponse(): hooks.Response<api.Addresses<EXTRA_FIELDS>> {
    if (userId === undefined) {
      return { status: hooks.QueryStatus.PRISTINE }
    }

    return sharedQuery.getCurrentResponse([userId, extraFields])
  }

  async function create(address: api.CreateAddress) {
    if (userId === undefined) {
      return
    }

    const promiseResult = base.create(userId, address, extraFields)
    await sharedQuery.patch([userId, extraFields], async (response) => {
      const result = await promiseResult

      return result.next((address) => {
        const addresses = [...(response.data?.addresses ?? [])]

        addresses.push(address)

        return { addresses }
      })
    })

    return await promiseResult
  }

  function update(
    id: number,
    address: api.UpdateAddress,
  ): Promise<api.HttpResult<api.Addresses<EXTRA_FIELDS>> | undefined> | undefined {
    if (userId === undefined) {
      return
    }

    return sharedQuery.patch([userId, extraFields], async (response) => {
      const result = await base.update(id, address, extraFields)

      return result.next((address) => {
        const addresses = [...(response.data?.addresses ?? [])]

        const addressIndex = addresses.findIndex((address) => address.id === id)

        addresses[addressIndex] = address

        return { addresses }
      })
    })
  }

  function remove(id: number) {
    if (userId === undefined) {
      return
    }

    return sharedQuery.patch([userId, extraFields], async (response) => {
      const result = await base.remove(id)

      return result.next(() => {
        const addresses = [...(response.data?.addresses ?? [])]

        const addressIndex = addresses.findIndex((address) => address.id === id)

        addresses.splice(addressIndex, 1)

        return { addresses }
      })
    })
  }
}

function id<EXTRA_FIELDS extends string>(userId: number | undefined, extraFields: EXTRA_FIELDS[]): string {
  return JSON.stringify([userId, extraFields.sort()])
}
